import { Box, Checkbox, Stack, useTheme } from '@mui/material'
import { Circle, PencilSimple, Trash } from '@phosphor-icons/react'
import {
    AreaValue,
    AreaValueDataObject,
    assembleAssetTitle,
    assembleNameWithEmailFallback,
    dateDifference,
    decimalToLocalizedString,
    isoDateStringToLocalFormat,
    joinDefinedStrings,
    monetaryToLocalizedString,
    MonetaryValueDataObjectType,
    OptionsMenu,
    tokens,
} from 'ComponentLibrary'
import { Link } from 'ComponentLibrary/src/piazza/text/Link/Link'

import { compact } from 'lodash'
import { ReactElement, useContext } from 'react'
import { TableVariantContext } from '../../TableVariantContext'
import { SetOptional } from 'type-fest'
import { PlainLink } from 'ComponentLibrary/src/atoms/Link'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'

export const EmptyCell = ({ textAlign, showDash = true }: { textAlign?: 'start' | 'end' | 'center'; showDash?: boolean }) => (
    <Stack textAlign={textAlign} color={tokens.ColorTextDefault}>
        {showDash && '-'}
    </Stack>
)

export const TextCell = ({ text, textColor, maxWidth, bold }: { text?: string; textColor?: string; maxWidth?: string; bold?: boolean }) => {
    return text ? (
        <Stack display="block" width="100%" textAlign="start" maxWidth={maxWidth}>
            <TextWithEllipsis typographyProps={{ variant: 'textXS', color: textColor, bold }} tooltipText={text}>
                {text}
            </TextWithEllipsis>
        </Stack>
    ) : (
        <EmptyCell textAlign="start" />
    )
}

export const NumberCell = ({
    number,
    isInteger = false,
    align = 'flex-end',
    unit,
    textColor,
    locale,
}: {
    number?: number
    isInteger?: boolean
    align?: Align
    unit?: string
    textColor?: string
    locale?: string
}) => {
    return number !== undefined ? (
        <Stack gap={tokens.SizingSpacing050} direction="row" justifyContent={align} color={textColor}>
            {decimalToLocalizedString(number, isInteger, undefined, undefined, locale)}
            {unit && <Stack color="inherit">{unit}</Stack>}
        </Stack>
    ) : (
        <EmptyCell textAlign={align === 'flex-end' ? 'end' : align === 'flex-start' ? 'start' : align} />
    )
}

type BaseValueCellProps<TValue> = TValue extends string
    ? {
          value?: TValue
          toDisplayValue?: ToDisplayValueCallback<TValue>
          unit?: string
          valueDescriptor?: string
          isMetadata?: boolean
          align?: Align
          bold?: boolean
          prefix?: string
      }
    : {
          value?: TValue
          toDisplayValue: ToDisplayValueCallback<TValue>
          unit?: string
          valueDescriptor?: string
          isMetadata?: boolean
          align?: Align
          bold?: boolean
          prefix?: string
      }

type ToDisplayValueCallback<TValue> = (value: TValue) => string | null

export const BaseValueCell = <TValue,>({
    value,
    unit,
    valueDescriptor,
    isMetadata = false,
    toDisplayValue,
    align = 'flex-end',
    bold = false,
    prefix,
}: BaseValueCellProps<TValue>) => {
    const theme = useTheme()
    const isSimple = useContext(TableVariantContext) === 'simple'
    const mutedColor = isSimple ? tokens.ColorTextSubtle : theme.palette.customtext.black54
    const gap = isSimple ? tokens.SizingSpacing075 : tokens.SizingSpacing050
    if (value === undefined) {
        return (
            <Stack justifyContent={align} direction="row" gap={gap}>
                <Box color="inherit">-</Box>
                {unit && <Box color={mutedColor}>{unit}</Box>}
            </Stack>
        )
    }

    const displayValue = toDisplayValue?.(value) || value

    return (
        <Stack justifyContent={align} direction="row" gap={gap}>
            {prefix && (
                <Stack color={isMetadata ? mutedColor : 'inherit'} fontWeight={bold ? 500 : 'inherit'}>
                    {prefix}
                </Stack>
            )}
            <Stack color={isMetadata ? mutedColor : 'inherit'} fontWeight={bold ? 500 : 'inherit'}>
                {displayValue}
            </Stack>
            {unit && <Stack color={isSimple || isMetadata ? mutedColor : 'inherit'}>{unit}</Stack>}
            {valueDescriptor && <Stack color={mutedColor}>{valueDescriptor}</Stack>}
        </Stack>
    )
}

export const AreaCell = ({
    area,
    valueDescriptor,
    isMetadata,
    align,
    bold,
    locale,
}: {
    area?: AreaValueDataObject
    valueDescriptor?: string
    isMetadata?: boolean
    align?: Align
    bold?: boolean
    locale?: string
}) => {
    if (!area) {
        return <EmptyCell />
    }

    const areaValue = AreaValue.fromDataObject(area)

    return (
        <BaseValueCell
            value={decimalToLocalizedString(area.value, false, undefined, undefined, locale)}
            unit={areaValue.unitSymbol(locale)}
            valueDescriptor={valueDescriptor}
            isMetadata={isMetadata}
            align={align}
            bold={bold}
        />
    )
}

export type Align = 'flex-start' | 'center' | 'flex-end'
type CellPercentage = {
    percentage: number
    additionalUnit?: string
}

export const PercentageCell = ({
    percentage,
    additionalUnit,
    align = 'flex-end',
    isMetadata,
    bold,
    locale,
}: SetOptional<CellPercentage, 'percentage'> & {
    align?: Align
    isMetadata?: boolean
    bold?: boolean
    locale?: string
}) => {
    const isSimple = useContext(TableVariantContext) === 'simple'
    return (
        <BaseValueCell
            value={percentage}
            toDisplayValue={(value: number) =>
                decimalToLocalizedString(value * 100, false, isSimple ? 2 : 4, isSimple ? 2 : undefined, locale)
            }
            unit={joinDefinedStrings(['%', additionalUnit], ' ')}
            align={align}
            isMetadata={isMetadata}
            bold={bold}
        />
    )
}

export const MultiPercentageCell = ({
    percentages,
    align = 'flex-end',
    bold,
    locale,
}: {
    percentages?: CellPercentage[]
    align?: Align
    bold?: boolean
    locale?: string
}) => {
    if (!percentages) {
        return <EmptyCell />
    }

    return (
        <Stack direction="column">
            {percentages.map((item, index) => (
                <PercentageCell
                    key={`Prozentsatz ${index} - ${item.percentage}%`}
                    percentage={item.percentage}
                    additionalUnit={item.additionalUnit}
                    align={align}
                    bold={bold}
                    locale={locale}
                />
            ))}
        </Stack>
    )
}

export const MonetaryCell = ({
    monetaryValue,
    additionalUnit = '',
    align = 'flex-end',
    rounded = false,
    isMetadata = false,
    bold = false,
    prefix,
    locale,
}: {
    monetaryValue?: MonetaryValueDataObjectType
    additionalUnit?: string
    align?: Align
    rounded?: boolean
    isMetadata?: boolean
    bold?: boolean
    prefix?: string
    locale?: string
}) => {
    const unit = joinDefinedStrings([monetaryValue?.currency === 'EUR' ? '€' : monetaryValue?.currency || '€', additionalUnit], '')
    return (
        <BaseValueCell
            value={monetaryValue}
            toDisplayValue={(value: MonetaryValueDataObjectType) => monetaryToLocalizedString(value, false, rounded, locale)}
            align={align}
            unit={unit}
            isMetadata={isMetadata}
            bold={bold}
            prefix={prefix}
        />
    )
}

export const CheckboxCell = ({ isChecked }: { isChecked: boolean }) => {
    return (
        <Stack display="block" width="100%" textAlign="start">
            <Checkbox disabled={true} checked={isChecked} style={{ padding: 0 }} size="small" />
        </Stack>
    )
}

export const ActionsCell = ({
    onEdit,
    onDelete,
    rowIndex,
    withoutStyling,
    copy,
    disabled,
}: {
    onEdit?: () => void
    onDelete?: () => void
    rowIndex: number
    withoutStyling?: boolean
    copy?: Partial<ActionsCellCopy>
    disabled?: boolean
}) => {
    const isSimple = useContext(TableVariantContext) === 'simple'
    const resolvedCopy = { ...DEFAULT_ACTIONS_CELL_COPY, ...copy }
    const options = [
        onEdit && {
            title: resolvedCopy.edit,
            callback: onEdit,
            icon: <PencilSimple color={tokens.ColorIconBoldest} size={tokens.SizingIconS} />,
            disabled,
        },
        onDelete && {
            title: resolvedCopy.delete,
            callback: onDelete,
            danger: true,
            icon: <Trash color={tokens.ColorIconDanger} size={tokens.SizingIconS} />,
            disabled,
        },
    ]
    return (
        <div
            style={
                withoutStyling
                    ? {}
                    : isSimple
                      ? { display: 'flex', justifyContent: 'center' }
                      : { position: 'absolute', top: '25%', left: '25%' }
            }
        >
            <OptionsMenu options={compact(options)} ariaLabel={resolvedCopy.ariaLabel(rowIndex)} disabled={disabled} />
        </div>
    )
}

export type ActionsCellCopy = {
    edit: string
    delete: string
    ariaLabel: (rowIndex: number) => string
}

const DEFAULT_ACTIONS_CELL_COPY: ActionsCellCopy = {
    edit: 'Eintrag bearbeiten',
    delete: 'Eintrag Löschen',
    ariaLabel: (rowIndex) => `Reihe-${rowIndex} Optionen`,
}

type Address = {
    country: string
    city: string
    street: string
    house_number?: string
    state: string
    zip: string
}

export function AssetCell({ address, customerAssetReference }: { address?: Address; customerAssetReference?: string }) {
    const assetTitle = assembleAssetTitle({ ...address, customer_asset_reference: customerAssetReference })
    return <TextCell text={assetTitle} />
}

export function AddressCell({ address }: { address?: Address }) {
    const addressLineOne = joinDefinedStrings([address?.street, address?.house_number])
    return <TextCell maxWidth="200px" text={addressLineOne} />
}

type UserInfo = {
    first_name?: string
    last_name?: string
    email: string
}

export function UserCell({ userInfo, textColor }: { userInfo?: UserInfo; textColor?: string }) {
    return <TextCell maxWidth="200px" text={getUserDisplayText(userInfo)} textColor={textColor} />
}

function getUserDisplayText(userInfo?: UserInfo) {
    return userInfo ? assembleNameWithEmailFallback(userInfo) : undefined
}

export function DateCell({
    date,
    textColor,
    withTime,
    locale,
}: {
    date?: string
    textColor?: string
    withTime?: boolean
    locale?: string
}) {
    const localizedDateString = isoDateStringToLocalFormat(date, !!withTime, locale)
    return <TextCell maxWidth="200px" text={localizedDateString} textColor={textColor} />
}

export function DateDifferenceCell({
    date,
    maxDaysToDisplay,
    formatDifferenceText,
}: {
    date?: string
    maxDaysToDisplay?: number
    formatDifferenceText?: (days: number, maxDaysToDisplay?: number) => string
}) {
    const difference = date ? dateDifference({ date, maxDaysToDisplay }) : undefined
    const badgeColor = getDueDateBadgeColor(difference?.days)
    const text = difference ? (formatDifferenceText ? formatDifferenceText(difference.days, maxDaysToDisplay) : difference.text) : ''
    return difference ? (
        <Stack width="100%" direction="row" justifyContent="flex-start" alignItems="center" gap={tokens.SizingSpacing075}>
            {badgeColor && <Circle weight="fill" color={badgeColor} size={13} />}
            <TextWithEllipsis typographyProps={{ variant: 'textXS', color: tokens.ColorTextDefault }} tooltipText={text}>
                {text}
            </TextWithEllipsis>
        </Stack>
    ) : (
        <EmptyCell textAlign="start" />
    )
}

function getDueDateBadgeColor(daysRemaining?: number): string | undefined {
    if (daysRemaining === undefined) {
        return undefined
    }
    if (daysRemaining < 0) {
        return tokens.ColorIconDanger
    }

    if (daysRemaining < 7) {
        return tokens.ColorIconWarning
    }

    return tokens.ColorIconPositive
}

export function LinkCell({
    text,
    path,
    icon,
    ellipsis,
    maxWidth,
}: {
    text: string
    path: string
    icon?: ReactElement
    ellipsis?: boolean
    maxWidth?: string
}) {
    return (
        <Stack direction="row" maxWidth={ellipsis ? maxWidth || '120px' : '100%'} alignItems="center" gap={tokens.SizingSpacing075}>
            {icon}
            <Link href={path} label={text} ellipsis={ellipsis} />
        </Stack>
    )
}

export function PlainLinkCell({
    text,
    onClick,
    maxWidth,
    color,
    isStrikethrough,
}: {
    text: string
    onClick: () => void
    maxWidth?: string
    color?: string
    isStrikethrough?: boolean
}) {
    return (
        <Stack direction="row" maxWidth={maxWidth}>
            <PlainLink onClick={onClick} textAlign="left" maxWidth={maxWidth}>
                <TextWithEllipsis tooltipText={text} typographyProps={{ variant: 'link', color }}>
                    {isStrikethrough ? <del>{text}</del> : <>{text}</>}
                </TextWithEllipsis>
            </PlainLink>
        </Stack>
    )
}
